<template>
  <div class="report-page" :class="{ 'with-breadcrumb': isEmbedMode }">
    <!-- Breadcrumb for iframe mode -->
    <div v-if="isEmbedMode" class="breadcrumb-strip">
      <span class="breadcrumb-item clickable" @click="goToScans">All Scans</span>
      <span class="breadcrumb-separator">›</span>
      <span class="breadcrumb-item current">View Report</span>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-container">
      <div class="loading-spinner">
        <svg class="spinner" width="40" height="40" viewBox="0 0 40 40">
          <circle class="path" cx="20" cy="20" r="16" fill="none" stroke-width="3"></circle>
        </svg>
      </div>
      <p>Loading grid scan data...</p>
    </div>

    <!-- No Data State - When no executions have run -->
    <div v-else-if="!isLoading && !hasData" class="no-data-container">
      <div class="no-data-card">
        <div class="no-data-icon">📊</div>
        <h2>Grid Scan Not Yet Executed</h2>
        <p>This grid scan is scheduled but hasn't run yet. The first execution will happen automatically based on your schedule settings.</p>
        
        <!-- <div class="schedule-info">
          <div class="schedule-item">
            <span class="schedule-label">Scan ID:</span>
            <span class="schedule-value">{{ $route.params.id }}</span>
          </div>
        </div> -->
        
        <button class="back-btn" @click="$router.push('/scans')">
          <i class="fas fa-arrow-left"></i>
          Back to Scans
        </button>
      </div>
    </div>

    <!-- Main Content - Show when data is available -->
    <template v-else>
      <!-- Dark Blue Header -->
      <div class="dark-header">
        <div class="header-container">
          <div class="header-row">
            <div class="logo-text">Grid Rank</div>
            <div class="header-buttons">
              <button class="btn-share" @click="shareReport">
                <i class="fas fa-share-alt"></i>
                <span>Share</span>
              </button>
              <button class="btn-download" @click="downloadPDF">
                <i class="fas fa-download"></i>
                <span>Download PDF</span>
              </button>
            </div>
          </div>
          
          <div class="report-title">
            <h1>Grid Scan Performance Report</h1>
            <p>Comprehensive analysis of your geographic search performance</p>
          </div>
        </div>
      </div>

      <!-- Main Content Area -->
      <div class="content-wrapper">
      <!-- Business Info Card -->
      <div class="business-info-card">
        <div class="business-content p-3">
          <div class="business-left">
            <h2 class="business-name">{{ businessInfo.business_name }}</h2>
            <a :href="businessInfo.website" target="_blank" class="business-url" v-if="businessInfo.website">{{ businessInfo.website }}</a>
            
            <div class="info-grid">
              <div class="info-group">
                <label>Address</label>
                <div class="info-value">{{ formatAddress(businessInfo) }}</div>
              </div>
              
              <div class="info-group">
                <label>Phone Number</label>
                <div class="info-value">{{ businessInfo.phone }}</div>
              </div>
              
              <div class="info-group">
                <label>Created Date</label>
                <div class="info-value">{{ formatDate(this.completeData.createdAt) }}</div>
              </div>
            </div>
            
            <div class="info-grid pt-5" style="margin-top: 20px;">
              <div class="info-group">
                <label>Primary Category</label>
                <div class="info-value">{{ filterCategory(businessInfo.category) || 'Not Found' }}</div>
              </div>

              <div class="info-group">
                <label>Sub category</label>
                <div class="info-value">{{ businessInfo.subcategory || 'Not Found' }}</div>
              </div>
              
              <div class="info-group">
                <label>Map Grid</label>
                <div class="info-value">Square : {{ businessInfo.grid_size }} ({{ totalLocations }} pins), {{ businessInfo.radius }} {{ businessInfo.radius_unit }} distance between pins</div>
              </div>
            </div>
          </div>
          
          <div class="business-right">
            <div class="business-photo-container">
              <img v-if="businessPhoto" 
                   :src="businessPhoto" 
                   :alt="businessInfo.business_name"
                   class="business-photo"
                   @error="handlePhotoError">
              <div v-else class="photo-placeholder">
                <i class="fas fa-store"></i>
                <span>No photo available</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Stats Grid -->
      <div class="stats-container">
        <!-- Row 1 -->
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Keywords Tracked</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ keywordCount }}</span>
              <!-- <span class="stat-change positive">↑ 3.2%</span> -->
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Locations Checked</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ totalLocations }}</span>
              <!-- <span class="stat-change positive">↑ 10.5%</span> -->
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Average Position</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ avgRank }}</span>
              <!-- <span class="stat-change positive">↑ 5.7%</span> -->
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Overall Coverage</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ Math.round(visibilityPercent) }}%</span>
              <!-- <span class="stat-change">↑ 12.3%</span> -->
            </div>
          </div>
        </div>
        
        <!-- Row 2 -->
        <div class="stats-row">
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Ranking 1-3</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ rankingStats.top }}</span>
              <!-- <span class="stat-change positive">↑ 12.4%</span> -->
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Ranking 4-10</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ rankingStats.high }}</span>
              <!-- <span class="stat-change positive">↑ 10.2%</span> -->
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Ranking 11-20</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ rankingStats.medium }}</span>
              <!-- <span class="stat-change positive">↑ 6.8%</span> -->
            </div>
          </div>
          
          <div class="stat-card">
            <div class="stat-header">
              <span class="stat-label">Not Ranking</span>
            </div>
            <div class="stat-content">
              <span class="stat-number">{{ rankingStats.notRanked }}</span>
              <!-- <span class="stat-change negative">↓ 10.0%</span> -->
            </div>
          </div>
        </div>
      </div>

      <!-- Summary and Chart -->
      <div class="summary-chart-container">
        <div class="summary-section">
          <h3>Summary</h3>
          <p>
            Overall the business <strong>{{ businessInfo.business_name || 'Business' }}</strong> has a grid scan showing <strong>{{ Math.round(visibilityPercent) }}%</strong> of keyword-location combinations 
            ranking in top 20 positions across {{ totalLocations }} local grid locations and {{ keywordCount }} keyword{{ keywordCount > 1 ? 's' : '' }}. Average ranking position is <strong>{{ avgRank }}</strong>.
          </p>
          <div class="key-highlights">
            Key highlights: Grid coverage reached {{ Math.round(visibilityPercent) }}% visibility across all tracked keyword-location combinations.
          </div>
        </div>
        
        <div class="chart-section">
          <h3>Grid Coverage</h3>
          <div class="chart-wrapper p-2 mt-5">
            <div class="chart-left">
              <div class="donut-chart-container">
                <canvas ref="coverageChart"></canvas>
              </div>
            </div>
            <div class="chart-right">
              <div class="chart-stats-row">
                <div class="chart-stat-item">
                  <div class="chart-stat-value" style="color: #3b82f6;">{{ topPercent }}%</div>
                  <div class="chart-stat-label">High (1-5)</div>
                </div>
                <div class="chart-stat-item">
                  <div class="chart-stat-value" style="color: #f59e0b;">{{ highPercent }}%</div>
                  <div class="chart-stat-label">Medium (6-14)</div>
                </div>
                <div class="chart-stat-item">
                  <div class="chart-stat-value" style="color: #FF0000;">{{ mediumPercent }}%</div>
                  <div class="chart-stat-label">Low (15-20)</div>
                </div>
                <div class="chart-stat-item">
                  <div class="chart-stat-value" style="color: #dcdbdb;">{{ notRankedPercent }}%</div>
                  <div class="chart-stat-label">Out (20+)</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map Section -->
      <div class="map-section">
        <div class="map-header">
          <h3>Average Geographic Performance Overview</h3>
          <div class="map-legend-inline">
            <div class="legend-item">
              <span class="legend-square" style="background: #3EBB80;"></span>
              <span class="legend-text">Rankings: 1-5</span>
            </div>
            <div class="legend-item">
              <span class="legend-square" style="background: #f59e0b;"></span>
              <span class="legend-text">Rankings: 6-14</span>
            </div>
            <div class="legend-item">
              <span class="legend-square" style="background: #FF0000;"></span>
              <span class="legend-text">Rankings: 15-20</span>
            </div>
            <div class="legend-item">
              <span class="legend-square" style="background: #dcdbdb;"></span>
              <span class="legend-text">Not Ranking</span>
            </div>
          </div>
        </div>
        <div class="map-container">
          <div ref="performanceMap" class="performance-map"></div>
        </div>
      </div>

      <!-- Competitors Table -->
      <div class="table-section">
        <h3>Overall Top Ranking Competitors for {{ formatKeywordsForDisplay() }}</h3>
        <p class="table-description">Your competitors' Google Business Profile (GBP) data shown is the average across multiple locations</p>
        
        <div class="competitors-table-wrapper">
          <table class="data-table">
          <thead>
            <tr>
              <th>Business Name</th>
              <th>Avg. Rank</th>
              <th>Reviews</th>
              <th>Rating</th>
              <th>Primary Category</th>
              <th>Additional Categories</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(competitor, index) in topCompetitors" :key="index">
              <td>
                {{ competitor.name }}
                <span v-if="competitor.isMainBusiness" class="you-badge">(You)</span>
              </td>
              <td class="text-center">{{ competitor.avgRank ? competitor.avgRank.toFixed(1) : '-' }}</td>
              <td class="text-center">{{ competitor.reviews || 0 }}</td>
              <td>
                <div class="rating-cell">
                  <span class="stars">
                    <i v-for="n in 5" :key="n" 
                       class="fas fa-star" 
                       :class="{ 'filled': n <= Math.floor(competitor.rating || 0) }"></i>
                  </span>
                  {{ competitor.rating ? competitor.rating.toFixed(1) : '0.0' }}
                </div>
              </td>
              <td>{{ filterCategory(competitor.primaryCategory) || '-' }}</td>
              <td>
                <div v-if="filterCategoriesArray(competitor.additionalCategoriesArray).length > 0">
                  <!-- For PDF export, show all categories -->
                  <span class="pdf-only">{{ filterCategoriesArray(competitor.additionalCategoriesArray).join(' , ') }}</span>
                  <!-- For screen display, show with tooltip -->
                  <span class="screen-only">
                    <span>{{ filterCategoriesArray(competitor.additionalCategoriesArray)[0] }}</span>
                    <span
                      v-if="filterCategoriesArray(competitor.additionalCategoriesArray).length > 1"
                      style="color: #2477f6; cursor: pointer; font-weight: 500;"
                      @mouseenter="showCategoryTooltip($event, filterCategoriesArray(competitor.additionalCategoriesArray).slice(1))"
                      @mouseleave="hideCategoryTooltip">
                      <br>+{{ filterCategoriesArray(competitor.additionalCategoriesArray).length - 1 }} more
                    </span>
                  </span>
                </div>
                <span v-else>-</span>
              </td>
            </tr>
          </tbody>
        </table>
        </div>
      </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios'
import Chart from 'chart.js'
import html2canvas from 'html2canvas'
import jsPDF from 'jspdf'
import embedMode from '@/mixins/embedMode'

export default {
  name: 'RecurringReport',
  mixins: [embedMode],
  metaInfo() {
    return {
      title: "Recurring Report | " + this.$appName
    }
  },
  data() {
    return {
      isLoading: true,
      hasData: false,
      completeData:{},
      searchProvider: null, // Store the search provider (google_places or value_serp)
      businessInfo: {},
      businessPhoto: null,
      googleMapsApiKey: null, // Store API key from backend
      processItems: [],
      topCompetitors: [],
      chart: null,
      map: null,
      markers: [],
      // primaryKeyword removed - using completeData.keywords instead
      keywordCount: 0,
      avgRank: '0',
      visibilityPercent: 0,
      totalLocations: 0,
      rankingStats: {
        top: 0,
        high: 0,
        medium: 0,
        notRanked: 0
      },
      // Percentages for chart display
      topPercent: 0,
      highPercent: 0,
      mediumPercent: 0,
      notRankedPercent: 0
    }
  },
  async mounted() {
    // Track embed usage if in embedded mode
    if (this.isEmbedMode) {
      this.trackEmbedUsage('recurring-report');
    }

    await this.fetchScanData();
    this.loadGoogleMaps();
    this.$nextTick(() => {
      this.createCoverageChart();
    });
  },
  computed: {
    isEmbedMode() {
      return this.$store.getters['embed/isEmbedMode'];
    }
  },
  methods: {
    goToScans() {
      this.$router.push('/scans');
    },
    filterCategory(category) {
      // Hide "establishment" and "point of interest" categories
      if (!category) return '';
      const lowerCategory = category.toLowerCase();
      if (lowerCategory === 'establishment' || lowerCategory === 'point of interest') {
        return '';
      }
      return category;
    },

    filterCategoriesArray(categories) {
      // Filter out "establishment" and "point of interest" from arrays
      if (!categories || !Array.isArray(categories)) return [];
      return categories.filter(cat => {
        const lowerCat = cat.toLowerCase();
        return lowerCat !== 'establishment' && lowerCat !== 'point of interest';
      });
    },

    formatKeywordsForDisplay() {
      // Check if completeData and keywords exist
      if (!this.completeData || !this.completeData.keywords) {
        return "'keyword'";
      }

      let keywords = this.completeData.keywords;

      // Ensure keywords is an array
      if (!Array.isArray(keywords)) {
        if (typeof keywords === 'string') {
          try {
            keywords = JSON.parse(keywords);
          } catch (e) {
            // If parsing fails, treat as single keyword
            return `'${keywords}'`;
          }
        } else {
          return "'keyword'";
        }
      }

      // Format keywords for display
      if (keywords.length === 0) {
        return "'keyword'";
      }

      return keywords.map(k => `'${k}'`).join(', ');
    },
    formatAddress(businessInfo) {
      if (!businessInfo) return '';

      // If we have a full address, use it
      if (businessInfo.address) {
        return businessInfo.address;
      }

      // Build address from components
      const parts = [];
      if (businessInfo.city) parts.push(businessInfo.city);
      if (businessInfo.state) parts.push(businessInfo.state);
      if (businessInfo.postal_code) parts.push(businessInfo.postal_code);
      if (businessInfo.country) parts.push(businessInfo.country);

      // If we have any parts, join them
      if (parts.length > 0) {
        return parts.join(', ');
      }

      // Default fallback
      return businessInfo.country || 'Service Area Business';
    },

    constructAddress(fullDetails, businessData, scanData) {
      // Try formatted address first
      if (fullDetails.formatted_address || fullDetails.address) {
        return fullDetails.formatted_address || fullDetails.address;
      }
      if (businessData.address) {
        return businessData.address;
      }
      if (scanData.address) {
        return scanData.address;
      }

      // Build from components if no direct address
      const parts = [];
      const city = fullDetails.city || businessData.city || scanData.city;
      const state = fullDetails.state || businessData.state || scanData.state;
      const postal_code = fullDetails.postal_code || businessData.postal_code || scanData.postal_code;
      const country = fullDetails.country || businessData.country || scanData.country || 'United States';

      if (city) parts.push(city);
      if (state) parts.push(state);
      if (postal_code) parts.push(postal_code);
      if (country) parts.push(country);

      if (parts.length > 0) {
        return parts.join(', ');
      }

      // Default to country for SABs
      return country;
    },

    async fetchScanData() {
      try {
        this.isLoading = true;
        const scanId = this.$route.params.id;

        // Fetch latest execution data for recurring scan
        const response = await axios.get(
          `${process.env.VUE_APP_API_ENDPOINT}/api/scans/grid-scan/${scanId}/latest-execution`
        );

        if (response.data.success && response.data.data) {
          this.hasData = true;

          const scanData = response.data.data;

          // Store Google Maps API key from response
          if (response.data.googleMapsApiKey) {
            this.googleMapsApiKey = response.data.googleMapsApiKey;
          }

          // Store the search provider
          this.searchProvider = scanData.search_provider || 'google_places';

          // Extract business info from the search data
          if (scanData.business) {
            this.completeData = response.data.data;
            // Parse business JSON if it's a string
            const businessData = typeof scanData.business === 'string' 
              ? JSON.parse(scanData.business) 
              : scanData.business;
            
            // Check for nested full_details from ValueSERP
            const fullDetails = businessData.full_details || businessData;
            
            // Extract categories - check multiple possible locations
            let primaryCategory = '';
            let secondaryCategories = [];

            // Helper function to check if a category is generic/meaningless
            const isGenericCategory = (cat) => {
              if (!cat) return true;
              const lowerCat = cat.toLowerCase().replace(/_/g, ' ');
              return lowerCat === 'establishment' ||
                     lowerCat === 'point of interest' ||
                     lowerCat === 'point_of_interest' ||
                     lowerCat === 'store' ||
                     lowerCat === 'business';
            };

            // Helper function to find first meaningful category from an array
            const findFirstMeaningfulCategory = (cats) => {
              if (!cats || !Array.isArray(cats)) return '';
              for (const cat of cats) {
                if (cat && !isGenericCategory(cat)) {
                  return cat;
                }
              }
              return cats[0] || ''; // Fallback to first if all are generic
            };

            // Collect all possible category arrays
            let allCategories = [];

            // Priority 1: Check fullDetails sources
            if (fullDetails.primary_category && !isGenericCategory(fullDetails.primary_category)) {
              primaryCategory = fullDetails.primary_category;
            }
            if (fullDetails.category && !primaryCategory && !isGenericCategory(fullDetails.category)) {
              primaryCategory = fullDetails.category;
            }
            if (fullDetails.categories && Array.isArray(fullDetails.categories) && fullDetails.categories.length > 0) {
              allCategories = fullDetails.categories;
              if (!primaryCategory) {
                primaryCategory = findFirstMeaningfulCategory(fullDetails.categories);
              }
            }
            if (fullDetails.type && !primaryCategory && !isGenericCategory(fullDetails.type)) {
              primaryCategory = fullDetails.type;
            }
            if (fullDetails.types && Array.isArray(fullDetails.types) && fullDetails.types.length > 0) {
              if (allCategories.length === 0) {
                allCategories = fullDetails.types;
              }
              if (!primaryCategory) {
                primaryCategory = findFirstMeaningfulCategory(fullDetails.types);
              }
            }

            // Priority 2: Check businessData sources
            if (businessData.primary_category && !primaryCategory && !isGenericCategory(businessData.primary_category)) {
              primaryCategory = businessData.primary_category;
            }
            if (businessData.category && !primaryCategory && !isGenericCategory(businessData.category)) {
              primaryCategory = businessData.category;
            }
            if (businessData.categories && Array.isArray(businessData.categories) && businessData.categories.length > 0) {
              if (allCategories.length === 0) {
                allCategories = businessData.categories;
              }
              if (!primaryCategory) {
                primaryCategory = findFirstMeaningfulCategory(businessData.categories);
              }
            }
            if (businessData.type && !primaryCategory && !isGenericCategory(businessData.type)) {
              primaryCategory = businessData.type;
            }
            if (businessData.types && Array.isArray(businessData.types) && businessData.types.length > 0) {
              if (allCategories.length === 0) {
                allCategories = businessData.types;
              }
              if (!primaryCategory) {
                primaryCategory = findFirstMeaningfulCategory(businessData.types);
              }
            }

            // If no meaningful primary category found, use first available category (even if generic)
            // This ensures primary category is never empty if any category exists
            if (!primaryCategory && allCategories.length > 0) {
              primaryCategory = allCategories[0];
            }

            // Filter out generic categories from the array for secondary categories only
            const filteredCategories = allCategories.filter(cat => !isGenericCategory(cat));

            // Get ALL secondary categories (everything except the primary)
            secondaryCategories = filteredCategories.filter(cat => {
              const normalizedCat = (cat || '').toLowerCase().replace(/_/g, ' ');
              const normalizedPrimary = (primaryCategory || '').toLowerCase().replace(/_/g, ' ');
              return normalizedCat !== normalizedPrimary;
            });

            // Format secondary categories as comma-separated string
            const subcategory = secondaryCategories
              .map(cat => (cat || '').replace(/_/g, ' '))
              .join(', ');
            
            // Helper function to capitalize each word
            const capitalizeWords = (str) => {
              if (!str) return '';
              return str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
            };

            this.businessInfo = {
              business_name: fullDetails.name || fullDetails.title || businessData.name || scanData.business_name || 'Business Name',
              address: this.constructAddress(fullDetails, businessData, scanData),
              city: fullDetails.city || businessData.city || scanData.city || '',
              state: fullDetails.state || businessData.state || scanData.state || '',
              postal_code: fullDetails.postal_code || businessData.postal_code || scanData.postal_code || '',
              country: fullDetails.country || businessData.country || scanData.country || 'United States',
              phone: fullDetails.formatted_phone_number || fullDetails.phone || businessData.phone || '',
              website: fullDetails.website || businessData.website || '',
              place_id: fullDetails.placeId || fullDetails.place_id || businessData.place_id || '',
              category: primaryCategory ? capitalizeWords(primaryCategory) : '',
              subcategory: subcategory ? capitalizeWords(subcategory) : '',
              grid_size: businessData.grid_size || scanData.grid_size || '3x3',
              grid_shape: businessData.grid_shape || scanData.grid_shape || 'square',
              radius: businessData.radius || scanData.radius || 1,
              radius_unit: businessData.radius_unit || scanData.radius_unit || scanData.distance_type || 'mi',
              rating: businessData.rating || 0,
              reviews: businessData.user_ratings_total || 0,
              latitude: businessData.latitude || (businessData.location && businessData.location.lat),
              longitude: businessData.longitude || (businessData.location && businessData.location.lng),
              grid_points: businessData.grid_points || []
            };
            
            // Extract photo from Google Places or ValueSERP
            this.extractBusinessPhoto(fullDetails, businessData);
            

          }
          
          // Extract process items from ScanItems structure (same as ViewGrid)
          this.processItems = [];
          if (scanData.ScanItems && Array.isArray(scanData.ScanItems)) {
            // Get first ScanItem which contains all ProcessItems
            const firstScanItem = scanData.ScanItems[0];
            if (firstScanItem && firstScanItem.ProcessItems) {
              this.processItems = firstScanItem.ProcessItems;
            }
          } else if (scanData.searches && Array.isArray(scanData.searches)) {
            // Fallback to searches structure
            scanData.searches.forEach(search => {
              if (search.processItems && Array.isArray(search.processItems)) {
                this.processItems.push(...search.processItems);
              }
            });
          } else if (scanData.processItems) {
            this.processItems = scanData.processItems;
          }
          

          
          // Extract keywords
          let keywords = [];

          // First, check if keywords are directly in scanData (for grid scans)
          if (scanData.keywords) {
            // Parse keywords if they're stored as a string (e.g., "['roofing near me']")
            if (typeof scanData.keywords === 'string') {
              try {
                // Try to parse as JSON array
                keywords = JSON.parse(scanData.keywords);
              } catch (e) {
                // If parsing fails, check if it's a comma-separated string
                keywords = scanData.keywords.split(',').map(k => k.trim());
              }
            } else if (Array.isArray(scanData.keywords)) {
              keywords = scanData.keywords;
            }
          }

          // Fallback to searches structure if no keywords found
          if (keywords.length === 0 && scanData.searches && Array.isArray(scanData.searches)) {
            scanData.searches.forEach(search => {
              if (search.keyword && !keywords.includes(search.keyword)) {
                keywords.push(search.keyword);
              }
            });
          }

          // Store keywords in completeData for template access
          if (!this.completeData.keywords) {
            this.completeData.keywords = keywords;
          }
          
          this.keywordCount = keywords.length || 1;
          
          this.calculateStats();
          this.extractTopCompetitors();
          
          // Calculate total locations
          const gridSize = this.businessInfo.grid_size || scanData.grid_size || '3x3';
          const [rows, cols] = gridSize.split('x').map(n => parseInt(n));
          this.totalLocations = rows * cols;
          
          // Initialize map after data is loaded
          if (window.google && window.google.maps) {
            this.$nextTick(() => {
              this.initializeMap();
            });
          }
        }
      } catch (error) {
        console.error('Error fetching scan data:', error);
        this.hasData = false;
      } finally {
        this.isLoading = false;
      }
    },

    calculateStats() {
      let high = 0, medium = 0, low = 0, out = 0;
      let totalRank = 0;

      // Get total grid locations from grid_size
      const gridSize = this.businessInfo.grid_size || '3x3';
      const [rows, cols] = gridSize.split('x').map(n => parseInt(n) || 3);
      const totalGridLocations = rows * cols;

      this.processItems.forEach(item => {
        // For average calculation, treat rank 0 as rank 20
        const rankForAvg = (!item.rank || item.rank === 0) ? 20 : item.rank;
        totalRank += rankForAvg;

        // Use the same logic as ViewGrid - check item.rank directly
        if (!item.rank || item.rank === 0) {
          out++;
        } else if (item.rank <= 5) {
          high++;
        } else if (item.rank <= 14) {
          medium++;
        } else if (item.rank <= 20) {
          low++;
        } else {
          out++;
        }
      });

      // Add rank 20 for any missing locations (grid locations without process items)
      const missingLocations = totalGridLocations - this.processItems.length;
      if (missingLocations > 0) {
        totalRank += missingLocations * 20;
        out += missingLocations;
      }

      // For backward compatibility with existing template
      this.rankingStats = {
        top: high,  // Map high to top for existing template
        high: medium, // Map medium to high for existing template
        medium: low, // Map low to medium for existing template
        notRanked: out
      };

      const total = totalGridLocations || 1;

      // Calculate percentages
      this.topPercent = Math.round((high / total) * 100);
      this.highPercent = Math.round((medium / total) * 100);
      this.mediumPercent = Math.round((low / total) * 100);
      this.notRankedPercent = Math.round((out / total) * 100);

      // Overall visibility includes rankings 1-20
      const visible = high + medium + low;
      this.visibilityPercent = Math.round((visible / total) * 100);

      // Always divide by total grid locations
      this.avgRank = (totalRank / total).toFixed(2);

    },

    extractTopCompetitors() {
      const competitorsMap = new Map();
      const mainBusinessPlaceId = this.businessInfo.place_id;
      const mainBusinessName = this.businessInfo.business_name;

      this.processItems.forEach(item => {
        if (item.result) {
          let results = item.result;
          if (typeof results === 'string') {
            try {
              results = JSON.parse(results);
            } catch (e) {
              return;
            }
          }

          if (Array.isArray(results)) {
            results.forEach((business, index) => {
              const placeId = business.place_id;
              const rank = index + 1;

              if (placeId) {
                if (!competitorsMap.has(placeId)) {
                  const isMainBusiness = (mainBusinessPlaceId && placeId === mainBusinessPlaceId) ||
                                        (mainBusinessName && business.name &&
                                         business.name.toLowerCase() === mainBusinessName.toLowerCase());

                  // Extract categories based on the search provider
                  let primaryCategory = '';
                  let additionalCategories = '';
                  let additionalCategoriesArray = [];

                  if (this.searchProvider === 'google_places') {
                    // DataForSEO (google_places provider)
                    // Check in original_data first, then in the main object
                    if (business.original_data) {
                      primaryCategory = business.original_data.category || '';
                      if (business.original_data.additional_categories && Array.isArray(business.original_data.additional_categories)) {
                        additionalCategoriesArray = business.original_data.additional_categories;
                        additionalCategories = additionalCategoriesArray.join(', ');
                      }
                    } else {
                      // Fallback to main object
                      primaryCategory = business.category || '';
                      if (business.additional_categories && Array.isArray(business.additional_categories)) {
                        additionalCategoriesArray = business.additional_categories;
                        additionalCategories = additionalCategoriesArray.join(', ');
                      }
                    }
                  } else if (this.searchProvider === 'value_serp') {
                    // ValueSerp provider
                    if (business.original_data) {
                      primaryCategory = business.original_data.category || '';

                      // For additional categories, use all_categories and exclude the primary
                      if (business.original_data.all_categories && Array.isArray(business.original_data.all_categories)) {
                        // Filter out the primary category
                        additionalCategoriesArray = business.original_data.all_categories
                          .filter(cat => cat !== primaryCategory);
                        additionalCategories = additionalCategoriesArray.join(', ');
                      }
                    } else {
                      // Fallback if no original_data
                      primaryCategory = business.category || '';
                      if (business.all_categories && Array.isArray(business.all_categories)) {
                        additionalCategoriesArray = business.all_categories
                          .filter(cat => cat !== primaryCategory);
                        additionalCategories = additionalCategoriesArray.join(', ');
                      }
                    }
                  }

                  // Default if no category found
                  if (!primaryCategory) {
                    primaryCategory = 'Business';
                  }

                  competitorsMap.set(placeId, {
                    name: business.name || business.title || 'Unknown Business',
                    ranks: [],
                    rating: business.rating || 0,
                    reviews: business.user_ratings_total || business.reviews || 0,
                    primaryCategory: primaryCategory,
                    additionalCategories: additionalCategories,
                    additionalCategoriesArray: additionalCategoriesArray,
                    isMainBusiness: isMainBusiness
                  });
                }

                if (rank <= 20) {
                  competitorsMap.get(placeId).ranks.push(rank);
                }
              }
            });
          }
        }
      });

      const competitors = Array.from(competitorsMap.values())
        .filter(comp => comp.ranks.length > 0)
        .map(comp => ({
          ...comp,
          avgRank: comp.ranks.reduce((a, b) => a + b, 0) / comp.ranks.length
        }));

      this.topCompetitors = competitors
        .sort((a, b) => a.avgRank - b.avgRank)
        .slice(0, 10);
      
      this.competitorsCount = competitors.length;

    },

    loadGoogleMaps() {
      if (window.google && window.google.maps) {
        this.initializeMap();
        return;
      }

      const script = document.createElement('script');
      // Use API key from backend response (either user's key or public key)
      const apiKey = this.googleMapsApiKey;
      if (!apiKey) {
        console.error('No Google Maps API key available');
        // Don't show error for guest users, just skip map initialization
        return;
      }

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=initRecurringReportMap`;
      script.async = true;
      script.defer = true;

      window.initRecurringReportMap = () => {
        this.initializeMap();
      };
      
      document.head.appendChild(script);
    },

    initializeMap() {
      if (!this.$refs.performanceMap || !window.google) {
        console.error('Map container or Google Maps not available');
        return;
      }
      
      // Use business location as center, or calculate from grid points
      let centerLat = this.businessInfo.latitude;
      let centerLng = this.businessInfo.longitude;
      
      // If business location not available, calculate from process items
      if (!centerLat || !centerLng) {
        let sumLat = 0, sumLng = 0, count = 0;
        this.processItems.forEach(item => {
          if (item.latitude && item.longitude) {
            sumLat += parseFloat(item.latitude);
            sumLng += parseFloat(item.longitude);
            count++;
          }
        });
        if (count > 0) {
          centerLat = sumLat / count;
          centerLng = sumLng / count;
        } else {
          centerLat = 39.8698; // Default fallback
          centerLng = -75.7356;
        }
      }
      
      const mapOptions = {
        zoom: 13,
        center: { lat: parseFloat(centerLat), lng: parseFloat(centerLng) },
        mapTypeId: 'roadmap',
        disableDefaultUI: false,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'off' }]
          }
        ]
      };

      this.map = new google.maps.Map(this.$refs.performanceMap, mapOptions);
      
      // Add markers after map is initialized
      if (this.processItems && this.processItems.length > 0) {
        this.addPerformanceMarkers();
      }
    },

    addPerformanceMarkers() {
      // Clear existing markers first
      this.markers.forEach(marker => marker.setMap(null));
      this.markers = [];
      
      // Add grid overlay first (Rectangle or Circle)
      this.addGridOverlay();
      
      this.processItems.forEach((item, index) => {
        if (item.latitude && item.longitude) {
          const position = new google.maps.LatLng(
            parseFloat(item.latitude), 
            parseFloat(item.longitude)
          );
          
          // Use item.rank directly (same as ViewGrid)
          const rank = item.rank || 0;
          let displayText = '?';
          
          // Check if scan is completed for this item
          const isCompleted = item.status === 'completed';
          
          if (isCompleted) {
            if (rank === 0) {
              displayText = '20+';
            } else if (rank) {
              displayText = rank.toString();
            } else {
              displayText = 'NR';
            }
          }
          
          const color = isCompleted ? this.getRankColor(rank) : '#dcdbdb'; // Gray for loading
          
          // Create marker with text inside (same as ViewGrid)
          const marker = new google.maps.Marker({
            position: position,
            map: this.map,
            icon: {
              url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(this.createMarkerSVG(color, displayText))}`,
              scaledSize: new google.maps.Size(32, 48),
              anchor: new google.maps.Point(16, 48)
            },
            title: `Location ${index + 1}: Rank ${rank || 'Not Ranked'}`
          });
          
          this.markers.push(marker);
        }
      });
      
      if (this.markers.length > 0) {
        const bounds = new google.maps.LatLngBounds();
        this.markers.forEach(marker => {
          bounds.extend(marker.getPosition());
        });
        this.map.fitBounds(bounds);
        
        // Limit zoom
        const listener = google.maps.event.addListener(this.map, "idle", () => {
          if (this.map.getZoom() > 16) this.map.setZoom(16);
          google.maps.event.removeListener(listener);
        });
      }
    },
    
    createMarkerSVG(color, text) {
      return `
        <svg width="32" height="48" viewBox="0 0 32 48" xmlns="http://www.w3.org/2000/svg">
          <g>
            <path d="M16 0C7.16 0 0 7.16 0 16c0 11.2 14.4 30.4 15.36 31.52c0.16 0.16 0.4 0.32 0.64 0.32s0.48-0.16 0.64-0.32C17.6 46.4 32 27.2 32 16c0-8.84-7.16-16-16-16z" fill="${color}" stroke="#888888" stroke-width="1"/>
            <text x="16" y="21" text-anchor="middle" fill="#FFFFFF" font-size="13" font-weight="600" font-family="Arial" stroke="#333333" stroke-width="2" paint-order="stroke fill">${text}</text>
          </g>
        </svg>
      `;
    },
    
    addGridOverlay() {
      // Calculate bounds from process items
      if (!this.processItems || this.processItems.length === 0) return;

      const bounds = new google.maps.LatLngBounds();
      let centerLat = 0, centerLng = 0, validCount = 0;

      this.processItems.forEach(item => {
        if (item.latitude && item.longitude) {
          const lat = parseFloat(item.latitude);
          const lng = parseFloat(item.longitude);
          bounds.extend(new google.maps.LatLng(lat, lng));
          centerLat += lat;
          centerLng += lng;
          validCount++;
        }
      });

      if (validCount === 0) return;

      // Calculate actual center from process items
      centerLat /= validCount;
      centerLng /= validCount;
      const center = new google.maps.LatLng(centerLat, centerLng);
      
      // Get grid info from businessInfo or completeData
      // Check for grid_shape first, then grid_type as fallback
      const gridType = this.businessInfo.grid_shape || this.businessInfo.grid_type || this.businessInfo.gridType || 'square';

      // Draw grid overlay based on type
      if (gridType === 'circle' || gridType === 'circular') {
        // Calculate radius based on actual process items
        let maxDistance = 0;
        this.processItems.forEach(item => {
          if (item.latitude && item.longitude) {
            const distance = this.getDistanceFromLatLonInMeters(
              centerLat, centerLng,
              parseFloat(item.latitude), parseFloat(item.longitude)
            );
            maxDistance = Math.max(maxDistance, distance);
          }
        });

        // Add 10% padding to ensure all markers are inside
        const radius = maxDistance * 1;

        // Draw circle overlay
        new google.maps.Circle({
          center: center,
          radius: radius,
          fillColor: '#2477f6',
          fillOpacity: 0.05,
          strokeColor: '#2477f6',
          strokeOpacity: 0.8,
          strokeWeight: 2.5,
          map: this.map
        });
      } else {
        // Draw rectangle overlay with padding
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        // No padding to match the exact bounds
        new google.maps.Rectangle({
          bounds: {
            north: ne.lat(),
            south: sw.lat(),
            east: ne.lng(),
            west: sw.lng()
          },
          fillColor: '#2477f6',
          fillOpacity: 0.05,
          strokeColor: '#2477f6',
          strokeOpacity: 0.8,
          strokeWeight: 2.5,
          map: this.map
        });
      }
    },

    getDistanceFromLatLonInMeters(lat1, lon1, lat2, lon2) {
      const R = 6371000; // Radius of the earth in meters
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      const d = R * c;
      return d;
    },

    deg2rad(deg) {
      return deg * (Math.PI/180);
    },

    getRankColor(rank) {
      if (!rank || rank === 0 || rank > 20) return '#dcdbdb';
      if (rank <= 5) return '#3EBB80'; // Green for 1-5
      if (rank <= 14) return '#f59e0b'; // Orange for 6-14
      if (rank <= 20) return '#FF0000'; // Red for 15-20
      return '#dcdbdb';
    },

    createCoverageChart() {
      if (!this.$refs.coverageChart) return;
      
      const ctx = this.$refs.coverageChart.getContext('2d');
      
      if (this.chart) {
        this.chart.destroy();
      }
      
      this.chart = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: ['High', 'Medium', 'Low', 'Out'],
          datasets: [{
            data: [this.topPercent, this.highPercent, this.mediumPercent, this.notRankedPercent],
            backgroundColor: ['#3EBB80', '#f59e0b', '#FF0000', '#dcdbdb'],
            borderWidth: 0
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          legend: {
            display: false
          },
          tooltips: {
            callbacks: {
              label: function(tooltipItem, data) {
                const label = data.labels[tooltipItem.index];
                const value = data.datasets[0].data[tooltipItem.index];
                return label + ': ' + value + '%';
              }
            }
          },
          cutoutPercentage: 65
        }
      });
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    },
    
    extractBusinessPhoto(fullDetails, businessData) {
      // Try to get photo from Google Places data first
      if (businessData.photos && businessData.photos.length > 0) {
        const firstPhoto = businessData.photos[0];
        
        // Check if we have a stored URL from Google Places
        if (firstPhoto.url) {
          this.businessPhoto = firstPhoto.url;
        } else if (firstPhoto.photo_reference) {
          // Fallback to photo_reference if available (from Places API web service)
          const apiKey = this.googleMapsApiKey;
          if (apiKey) {
            this.businessPhoto = `https://maps.googleapis.com/maps/api/place/photo?maxwidth=400&photo_reference=${firstPhoto.photo_reference}&key=${apiKey}`;
          }
        }
      }
      
      // Fallback to ValueSERP image if available
      if (!this.businessPhoto) {
        // Check ValueSERP full_details for image
        if (fullDetails.image) {
          this.businessPhoto = fullDetails.image;
        } else if (fullDetails.images && fullDetails.images.length > 0) {
          this.businessPhoto = fullDetails.images[0];
        } else if (fullDetails.thumbnail) {
          this.businessPhoto = fullDetails.thumbnail;
        }
      }
      
      // Check other possible photo sources
      if (!this.businessPhoto) {
        if (businessData.image) {
          this.businessPhoto = businessData.image;
        } else if (businessData.thumbnail) {
          this.businessPhoto = businessData.thumbnail;
        }
      }
    },
    
    handlePhotoError() {
      // If photo fails to load, set to null to show placeholder
      this.businessPhoto = null;
    },

    shareReport() {
      // Copy current URL to clipboard
      const url = window.location.href;

      // Create a temporary textarea to copy the URL
      const textarea = document.createElement('textarea');
      textarea.value = url;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);

      // Select and copy the text
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);

      // Show success message
      this.$swal({
        icon: 'success',
        title: 'Link Copied!',
        text: 'Report link has been copied to clipboard',
        timer: 2000,
        showConfirmButton: false
      });
    },

    async downloadPDF() {
      try {
        // Show loading message
        this.$swal({
          title: 'Generating PDF...',
          text: 'Please wait while we prepare your report',
          allowOutsideClick: false,
          showConfirmButton: false,
          willOpen: () => {
            this.$swal.showLoading();
          }
        });

        // Get the report container
        const element = document.querySelector('.report-page');
        if (!element) {
          throw new Error('Report container not found');
        }

        // Add class to show PDF-only content
        element.classList.add('generating-pdf');

        // Hide action buttons
        const actionButtons = document.querySelector('.report-actions');
        if (actionButtons) {
          actionButtons.style.display = 'none';
        }

        // Handle business photo - store original state for restoration
        const businessPhotoContainer = document.querySelector('.business-photo-container');
        const businessPhoto = document.querySelector('.business-photo');
        let originalPhotoSrc = null;
        let originalPhotoHTML = null;

        // Store the original HTML content of the photo container
        if (businessPhotoContainer) {
          originalPhotoHTML = businessPhotoContainer.innerHTML;
        }

        if (businessPhoto && businessPhoto.src) {
          originalPhotoSrc = businessPhoto.src;

          try {
            // Create a canvas to properly capture the image
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Try different methods to get the image working
            let imageConverted = false;

            // Method 1: Try using the image that's already loaded in the DOM
            if (businessPhoto.complete && businessPhoto.naturalWidth > 0) {
              try {
                // Set canvas dimensions to match the actual image
                canvas.width = businessPhoto.naturalWidth || 400;
                canvas.height = businessPhoto.naturalHeight || 300;

                // Try to draw the already loaded image
                ctx.drawImage(businessPhoto, 0, 0, canvas.width, canvas.height);

                // Try to convert to data URL
                try {
                  const dataUrl = canvas.toDataURL('image/png');
                  // Create a new img element with the data URL
                  const newImg = document.createElement('img');
                  newImg.src = dataUrl;
                  newImg.className = businessPhoto.className;
                  newImg.style.cssText = businessPhoto.style.cssText;
                  businessPhoto.parentNode.replaceChild(newImg, businessPhoto);
                  imageConverted = true;
                } catch (e) {
                  console.log('Canvas tainted, trying alternative method');
                }
              } catch (e) {
                console.log('Could not draw existing image:', e);
              }
            }

            // Method 2: If Method 1 failed, try loading through a proxy or use as-is
            if (!imageConverted) {
              // For Google Maps API images, we can't bypass CORS
              // So we'll keep the original image and let html2canvas handle it
              // html2canvas has better CORS handling with useCORS and allowTaint options

              // Just ensure the image has proper attributes for html2canvas
              businessPhoto.setAttribute('crossorigin', 'anonymous');

              // If it's a Google API image, we might need to reload it
              if (originalPhotoSrc.includes('googleapis.com')) {
                // Create a new image element to try loading with CORS
                const tempImg = new Image();
                tempImg.crossOrigin = 'anonymous';

                await new Promise((resolve) => {
                  let loadTimeout = setTimeout(() => {
                    console.log('Image load timeout, using original');
                    resolve();
                  }, 2000);

                  tempImg.onload = () => {
                    clearTimeout(loadTimeout);
                    try {
                      // Try to draw this image
                      canvas.width = tempImg.width || 400;
                      canvas.height = tempImg.height || 300;
                      ctx.drawImage(tempImg, 0, 0, canvas.width, canvas.height);

                      // Try converting to data URL
                      const dataUrl = canvas.toDataURL('image/png');
                      businessPhoto.src = dataUrl;
                      imageConverted = true;
                    } catch (e) {
                      console.log('Still cannot convert image:', e);
                    }
                    resolve();
                  };

                  tempImg.onerror = () => {
                    clearTimeout(loadTimeout);
                    console.log('Failed to load image with CORS');
                    resolve();
                  };

                  // Try loading with a timestamp to bypass cache
                  tempImg.src = originalPhotoSrc + (originalPhotoSrc.includes('?') ? '&' : '?') + 't=' + Date.now();
                });
              }
            }

            // Method 3: If still no success, create a professional-looking placeholder
            if (!imageConverted && originalPhotoSrc.includes('googleapis.com')) {
              console.log('Creating canvas placeholder for Google API image');

              // Set reasonable dimensions
              canvas.width = 400;
              canvas.height = 300;

              // Create a gradient background
              const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
              gradient.addColorStop(0, '#ffffff');
              gradient.addColorStop(1, '#f0f4f8');
              ctx.fillStyle = gradient;
              ctx.fillRect(0, 0, canvas.width, canvas.height);

              // Add the business photo if we can access it from cache
              // This is a last resort - try to use the cached image data
              try {
                // Create a pattern background
                ctx.strokeStyle = 'rgba(36, 119, 246, 0.05)';
                ctx.lineWidth = 1;

                // Draw grid pattern
                for (let i = 0; i < canvas.width; i += 30) {
                  ctx.beginPath();
                  ctx.moveTo(i, 0);
                  ctx.lineTo(i, canvas.height);
                  ctx.stroke();
                }
                for (let i = 0; i < canvas.height; i += 30) {
                  ctx.beginPath();
                  ctx.moveTo(0, i);
                  ctx.lineTo(canvas.width, i);
                  ctx.stroke();
                }

                // Add business name in center
                ctx.fillStyle = '#2477f6';
                ctx.font = 'bold 22px Arial';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                const businessName = this.businessInfo.business_name || 'Business Location';

                // Word wrap for long names
                const maxWidth = canvas.width * 0.8;
                const words = businessName.split(' ');
                let lines = [];
                let currentLine = '';

                words.forEach(word => {
                  const testLine = currentLine + (currentLine ? ' ' : '') + word;
                  const metrics = ctx.measureText(testLine);
                  if (metrics.width > maxWidth && currentLine) {
                    lines.push(currentLine);
                    currentLine = word;
                  } else {
                    currentLine = testLine;
                  }
                });
                if (currentLine) lines.push(currentLine);

                // Draw text lines
                const lineHeight = 28;
                const startY = canvas.height / 2 - ((lines.length - 1) * lineHeight) / 2;

                lines.forEach((line, i) => {
                  ctx.fillText(line, canvas.width / 2, startY + i * lineHeight);
                });

                // Add location pin icon above text
                ctx.font = '36px Arial';
                ctx.globalAlpha = 0.3;
                ctx.fillText('📍', canvas.width / 2, startY - 40);
                ctx.globalAlpha = 1.0;

              } catch (e) {
                console.log('Error creating placeholder:', e);
              }

              // Convert canvas to data URL and replace image
              try {
                const dataUrl = canvas.toDataURL('image/png');
                businessPhoto.src = dataUrl;
              } catch (e) {
                console.log('Could not convert placeholder to data URL:', e);
              }
            }

          } catch (e) {
            console.log('Error handling business photo:', e);
            // Leave the original image as-is
          }
        }

        // Force map to refresh and wait for tiles to load
        if (this.map && window.google && window.google.maps) {
          // Trigger resize to ensure map is properly rendered
          window.google.maps.event.trigger(this.map, 'resize');

          // Pan slightly to force tile refresh
          const center = this.map.getCenter();
          this.map.panBy(1, 0);
          this.map.panBy(-1, 0);
          this.map.setCenter(center);
        }

        // Wait longer for map tiles and everything to load
        await new Promise(resolve => setTimeout(resolve, 3000));

        // Capture the page as canvas with special handling for maps
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          allowTaint: true, // Allow tainted for map tiles
          foreignObjectRendering: false,
          logging: false,
          backgroundColor: '#f5f5f5',
          windowWidth: element.scrollWidth,
          windowHeight: element.scrollHeight,
          onclone: async (clonedDoc, element) => {
            // Remove buttons from cloned document
            const clonedButtons = clonedDoc.querySelector('.report-actions');
            if (clonedButtons) {
              clonedButtons.remove();
            }

            // Remove share and download buttons
            const shareBtn = clonedDoc.querySelector('.btn-share');
            const downloadBtn = clonedDoc.querySelector('.btn-download');
            if (shareBtn) shareBtn.remove();
            if (downloadBtn) downloadBtn.remove();

            // Keep the business photo since we've converted it to canvas
            // The canvas data URL doesn't have CORS issues

            // Remove all Google Maps controls from cloned document
            const mapControls = clonedDoc.querySelectorAll('.gm-control-container, .gmnoprint, .gm-style-cc, .gm-bundled-control, .gm-svpc');
            mapControls.forEach(control => {
              if (control) control.remove();
            });

            // Wait for rendering
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        });

        // Try to convert to blob first (works even with tainted canvas)
        let pdfBlob;
        try {
          // Try normal export first
          const imgData = canvas.toDataURL('image/png');

          // Create PDF using jsPDF
          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgWidth = 210;
          const pageHeight = 297;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;

          // Fill first page with background color
          pdf.setFillColor(245, 245, 245); // #f5f5f5 in RGB
          pdf.rect(0, 0, 210, 297, 'F');

          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;

          while (heightLeft >= 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            // Fill each new page with background color
            pdf.setFillColor(245, 245, 245); // #f5f5f5 in RGB
            pdf.rect(0, 0, 210, 297, 'F');
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }

          // Generate filename
          const businessName = this.businessInfo.business_name || 'Report';
          const date = new Date().toISOString().split('T')[0];
          const filename = `${businessName.replace(/[^a-z0-9]/gi, '_')}_Recurring_Report_${date}.pdf`;

          // Download the PDF
          pdf.save(filename);

        } catch (err) {
          // If tainted canvas error, use blob conversion method
          if (err.message && err.message.includes('tainted')) {
            canvas.toBlob((blob) => {
              if (blob) {
                // Create image from blob
                const img = new Image();
                const url = URL.createObjectURL(blob);
                img.onload = () => {
                  // Create new canvas from image
                  const newCanvas = document.createElement('canvas');
                  newCanvas.width = canvas.width;
                  newCanvas.height = canvas.height;
                  const ctx = newCanvas.getContext('2d');
                  ctx.drawImage(img, 0, 0);

                  // Now export from clean canvas
                  const imgData = newCanvas.toDataURL('image/png');
                  const pdf = new jsPDF('p', 'mm', 'a4');
                  const imgWidth = 210;
                  const imgHeight = (newCanvas.height * imgWidth) / newCanvas.width;

                  pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);

                  // Generate filename
                  const businessName = this.businessInfo.business_name || 'Report';
                  const date = new Date().toISOString().split('T')[0];
                  const filename = `${businessName.replace(/[^a-z0-9]/gi, '_')}_Recurring_Report_${date}.pdf`;

                  pdf.save(filename);
                  URL.revokeObjectURL(url);
                };
                img.src = url;
              }
            }, 'image/png');
          } else {
            throw err;
          }
        }

        // Restore action buttons and photo
        if (actionButtons) {
          actionButtons.style.display = '';
        }

        // Restore the original photo container content
        const restoredPhotoContainer = document.querySelector('.business-photo-container');
        if (restoredPhotoContainer && originalPhotoHTML) {
          restoredPhotoContainer.innerHTML = originalPhotoHTML;
        }

        // Remove generating-pdf class
        element.classList.remove('generating-pdf');

        // Close loading
        this.$swal.close();

        // Show success message
        this.$swal({
          icon: 'success',
          title: 'PDF Downloaded!',
          text: 'Your report has been saved successfully',
          timer: 2000,
          showConfirmButton: false
        });

      } catch (error) {
        console.error('Error generating PDF:', error);

        // Restore elements on error
        const element = document.querySelector('.report-page');
        if (element) {
          element.classList.remove('generating-pdf');
        }
        if (actionButtons) {
          actionButtons.style.display = '';
        }

        // Restore the original photo container content
        const restoredPhotoContainer = document.querySelector('.business-photo-container');
        if (restoredPhotoContainer && originalPhotoHTML) {
          restoredPhotoContainer.innerHTML = originalPhotoHTML;
        }

        this.$swal({
          icon: 'error',
          title: 'PDF Generation Failed',
          text: 'Unable to generate PDF. Please try again.'
        });
      }
    },

    showCategoryTooltip(event, categories) {
      // Remove any existing tooltip
      this.hideCategoryTooltip();

      // Create tooltip element with inline styles
      const tooltip = document.createElement('div');
      tooltip.id = 'category-tooltip';

      // Apply inline styles to tooltip
      Object.assign(tooltip.style, {
        position: 'fixed',
        backgroundColor: '#19233A',
        color: 'white',
        padding: '20px 20px',
        borderRadius: '10px',
        fontSize: '12px',
        zIndex: '9999',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        maxWidth: '400px'
      });

      // Create category items with inline styling
      const categoriesHtml = categories.map(cat =>
        `<span style="
          display: inline-block;
          padding: 5px 15px;
          border: 1px solid white;
          border-radius: 10px;
          text-transform: capitalize;
          white-space: nowrap;
          font-size: 14px;
          margin: 0 3px;
        ">${cat}</span>`
      ).join('');

      tooltip.innerHTML = `
        <div style="display: flex; flex-wrap: wrap; gap: 6px; align-items: center;">
          ${categoriesHtml}
        </div>
      `;

      // Position tooltip at cursor position
      document.body.appendChild(tooltip);
      const tooltipRect = tooltip.getBoundingClientRect();

      // Get cursor position from event
      const cursorX = event.clientX;
      const cursorY = event.clientY;

      // Center tooltip horizontally on cursor
      let tooltipLeft = cursorX - (tooltipRect.width / 2);

      // Ensure tooltip doesn't go off screen
      if (tooltipLeft < 10) {
        tooltipLeft = 10;
      } else if (tooltipLeft + tooltipRect.width > window.innerWidth - 10) {
        tooltipLeft = window.innerWidth - tooltipRect.width - 10;
      }

      tooltip.style.left = tooltipLeft + 'px';
      tooltip.style.top = (cursorY + 15) + 'px'; // 15px below cursor

      // Add arrow pointing to cursor position
      const arrowLeft = Math.max(10, Math.min(tooltipRect.width - 10, cursorX - tooltipLeft));
      const arrow = document.createElement('div');
      Object.assign(arrow.style, {
        position: 'absolute',
        top: '-8px',
        left: (arrowLeft - 8) + 'px',
        width: '0',
        height: '0',
        borderLeft: '8px solid transparent',
        borderRight: '8px solid transparent',
        borderBottom: '8px solid #19233A'
      });
      tooltip.appendChild(arrow);
    },

    hideCategoryTooltip() {
      const tooltip = document.getElementById('category-tooltip');
      if (tooltip) {
        tooltip.remove();
      }
    }
  },
  
  beforeDestroy() {
    if (this.chart) this.chart.destroy();
    if (this.markers) this.markers.forEach(marker => marker.setMap(null));
    // Clean up any remaining tooltip
    this.hideCategoryTooltip();
  }
}
</script>

<style scoped>
/* Breadcrumb strip styles */
.breadcrumb-strip {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 30px;
  background-color: white;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  padding: 0 16px;
  z-index: 5;
  z-index: 1000;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
}

/* Hide breadcrumb during PDF generation */
.generating-pdf .breadcrumb-strip {
  display: none !important;
}

.breadcrumb-item {
  font-size: 12px;
  background: linear-gradient(90deg, #6b7280 0%, #9ca3af 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
}

.breadcrumb-item.clickable {
  background: linear-gradient(90deg, #4b5563 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  cursor: pointer;
  transition: all 0.2s;
}

.breadcrumb-item.clickable:hover {
  background: linear-gradient(90deg, #374151 0%, #4b5563 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: underline;
}

.breadcrumb-item.current {
  background: linear-gradient(90deg, #374151 0%, #6b7280 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 500;
}

.breadcrumb-separator {
  margin: 0 6px;
  color: #9ca3af;
  font-size: 14px;
}

* {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}
@import url(https://fonts.googleapis.com/css?family=Open+Sans:400,400italic,600,700,800);
.report-page {
  background: #f5f5f5;
  min-height: 100vh;
}

.report-page.with-breadcrumb {
  padding-top: 30px;
}

/* Remove padding-top during PDF generation */
.generating-pdf.report-page.with-breadcrumb {
  padding-top: 0 !important;
}

/* Dark Header */
.dark-header {
  color: white;
  background: #19233a;
  opacity: .9;
  height: auto;
  padding: 35px;
  padding-bottom: 200px;
}

.header-container {
  margin: 0 auto;
  padding: 0 20px;
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.logo-text {
  font-family: 'Rubik', sans-serif;
  font-size: 20px;
  font-weight: 500;
}

.header-buttons {
  display: flex;
  gap: 10px;
}

.btn-share,
.btn-download {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 13px;
  /* font-weight: 500; */
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  font-family: 'Rubik', sans-serif;
}

.btn-share {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
}

.btn-download {
  background: #3b82f6;
  color: white;
}

.report-title {
  text-align: center;
}

.report-title h1 {
  font-family: 'Rubik', sans-serif;
  font-size: 30px;
  font-weight: 500;
  margin-bottom: 6px;
}

.report-title p {
  font-size: 13px;
  opacity: 0.8;
}

/* Content Wrapper */
.content-wrapper {
  /* max-width: 1275px; */
  margin: -180px auto 0;
  padding: 0 20px 30px;
  position: relative;
  z-index: 1;
  padding: 10px 150px;
}

/* Business Info Card */
.business-info-card {
  background: white;
  border-radius: 6px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.1);
  margin-bottom: 20px;
  overflow: hidden;
}

.business-content {
  display: flex;
  height: 260px;
}

.business-left {
  flex: 1;
  padding: 20px 25px;
}

.business-name {
  font-size: 22px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
}

.business-url {
  color: #3b82f6;
  font-size: 12px;
  text-decoration: none;
  display: inline-block;
  margin-bottom: 15px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

.info-group label {
  display: block;
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 3px;
  font-weight: 500;
}

.info-value {
  font-size: 12px;
  color: #19233A;
  white-space: pre-line;
  line-height: 1.3;
}

.business-right {
  width: 340px;
  padding: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.business-photo-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden;
  background: #f8f9fa;
}

.business-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
}

.photo-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #9ca3af;
  text-align: center;
  padding: 20px;
}

.photo-placeholder i {
  font-size: 48px;
  color: #d1d5db;
}

.photo-placeholder span {
  font-size: 14px;
  color: #6b7280;
}

.browser-window {
  width: 310px;
  background: white;
  border-radius: 5px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0,0,0,0.2);
}

.browser-header {
  background: #f5f5f5;
  padding: 8px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e0e0e0;
}

.browser-controls {
  display: flex;
  gap: 6px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.dot.red { background: #ff5f57; }
.dot.yellow { background: #ffbd2e; }
.dot.green { background: #28ca42; }

.browser-address {
  flex: 1;
  text-align: center;
}

.address-text {
  font-size: 11px;
  color: #666;
}

.browser-menu {
  font-size: 10px;
  color: #666;
}

.browser-content {
  padding: 15px;
  height: 185px;
  position: relative;
  background: white;
}

.website-logo {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.kline-logo {
  text-align: left;
}

.logo-icon {
  font-size: 24px;
  margin-bottom: 5px;
}

.logo-text-main {
  font-size: 18px;
  font-weight: 600;
  color: #333;
}

.logo-text-sub {
  font-size: 9px;
  color: #666;
  letter-spacing: 1px;
}

.tagline {
  text-align: right;
  font-size: 12px;
  color: #ff4444;
  font-style: italic;
  line-height: 1.2;
}

.service-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
  margin-bottom: 12px;
}

.service-btn {
  height: 40px;
  border-radius: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 8px;
  font-weight: 600;
  text-align: center;
  background-size: cover;
  background-position: center;
}

.service-btn.metal {
  background: linear-gradient(rgba(255,152,0,0.8), rgba(255,152,0,0.8)), url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23ff9800" width="100" height="100"/></svg>');
}

.service-btn.shingle {
  background: linear-gradient(rgba(33,150,243,0.8), rgba(33,150,243,0.8)), url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%232196f3" width="100" height="100"/></svg>');
}

.service-btn.vinyl {
  background: linear-gradient(rgba(233,30,99,0.8), rgba(233,30,99,0.8)), url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23e91e63" width="100" height="100"/></svg>');
}

.service-btn.decks {
  background: linear-gradient(rgba(103,58,183,0.8), rgba(103,58,183,0.8)), url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect fill="%23673ab7" width="100" height="100"/></svg>');
}

.free-quote {
  position: absolute;
  bottom: 12px;
  right: 12px;
  background: #ff4444;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 3px;
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
}

/* Stats Container */
.stats-container {
  margin-bottom: 20px;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 12px;
}

.stat-card {
  background: white;
  border-radius: 6px;
  padding: 16px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.stat-card.highlighted {
  background: linear-gradient(135deg, #7c3aed 0%, #a855f7 100%);
}

.stat-header {
  margin-bottom: 8px;
}

.stat-label {
  font-size: 11px;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  font-weight: 500;
}

.stat-card.highlighted .stat-label {
  color: rgba(255,255,255,0.9);
}

.stat-content {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
}

.stat-number {
  font-size: 24px;
  font-weight: 600;
  color: #1a1a1a;
}

.stat-card.highlighted .stat-number {
  color: white;
}

.stat-change {
  font-size: 10px;
  font-weight: 500;
}

.stat-change.positive {
  color: #10b981;
}

.stat-change.negative {
  color: #ef4444;
}

.stat-card.highlighted .stat-change {
  color: rgba(255,255,255,0.9);
}

/* Summary and Chart */
.summary-chart-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 20px;
}

.summary-section,
.chart-section {
  background: white;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.summary-section h3,
.chart-section h3 {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.summary-section p {
  font-size: 12px;
  line-height: 1.5;
  color: #555;
  margin-bottom: 12px;
}

.key-highlights {
  padding: 8px 10px;
  background: #f0fdf4;
  border-left: 3px solid #10b981;
  border-radius: 3px;
  font-size: 11px;
  color: #065f46;
  line-height: 1.4;
}

.chart-wrapper {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 15px 0;
}

.chart-left {
  flex: 0 0 35%;
  display: flex;
  justify-content: center;
}

.chart-right {
  flex: 0 0 60%;
  display: flex;
  align-items: center;
}

.chart-stats-row {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.donut-chart-container {
  position: relative;
  width: 120px;
  height: 120px;
}

.donut-chart-container canvas {
  width: 100% !important;
  height: 100% !important;
}

.chart-stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  flex: 1;
}

.chart-stat-value {
  font-size: 15px;
  font-weight: 600;
  margin-bottom: 2px;
}

.chart-stat-label {
  font-size: 10px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

/* Map Section */
.map-section {
  background: white;
  border-radius: 8px;
  padding: 25px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  margin-bottom: 20px;
}

.map-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.map-section h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.map-legend-inline {
  display: flex;
  align-items: center;
  gap: 25px;
}

.map-container {
  position: relative;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.performance-map {
  width: 100%;
  height: 500px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.legend-square {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  border: 1px solid rgba(0,0,0,0.1);
  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
}

.legend-text {
  font-size: 12px;
  color: #4b5563;
  font-weight: 500;
}

/* Table Section */
.table-section {
  background: white;
  border-radius: 6px;
  padding: 20px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.table-section h3 {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
}

.table-description {
  font-size: 11px;
  color: #666;
  margin-bottom: 15px;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table th {
  text-align: left;
  padding: 8px;
  border-bottom: 2px solid #e5e5e5;
  font-size: 10px;
  font-weight: 600;
  color: #777;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  background: #fafafa;
}

.data-table td {
  padding: 10px 8px;
  border-bottom: 1px solid #f0f0f0;
  font-size: 12px;
  color: #333;
}

.text-center {
  text-align: center;
}

.you-badge {
  color: #3b82f6;
  font-size: 11px;
  font-weight: 500;
  margin-left: 5px;
}

.rating-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stars {
  display: flex;
  gap: 2px;
}

.stars i {
  font-size: 10px;
  color: #e0e0e0;
}

.stars i.filled {
  color: #fbbf24;
}
/* Loading and No Data States */
.loading-container, .no-data-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  background: #f9fafb;
}

.loading-spinner {
  margin-bottom: 20px;
}

.spinner {
  animation: rotate 2s linear infinite;
}

@keyframes rotate {
  100% {
    transform: rotate(360deg);
  }
}

.spinner .path {
  stroke: #3b82f6;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

.no-data-card {
  background: white;
  border-radius: 12px;
  padding: 48px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  max-width: 500px;
}

.no-data-icon {
  font-size: 48px;
  margin-bottom: 20px;
}

.no-data-card h2 {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 12px;
}

.no-data-card p {
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.6;
}

.schedule-info {
  background: #f8fafc;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
}

.schedule-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
}

.schedule-item:not(:last-child) {
  border-bottom: 1px solid #e5e7eb;
}

.schedule-label {
  font-size: 14px;
  color: #6b7280;
  font-weight: 500;
}

.schedule-value {
  font-size: 14px;
  color: #1f2937;
  font-weight: 600;
}

.back-btn {
  background: #3b82f6;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.back-btn:hover {
  background: #2563eb;
}

.back-btn i {
  font-size: 14px;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  /* Header adjustments */
  .dark-header {
    padding: 20px;
    padding-bottom: 150px;
  }

  .header-container {
    padding: 0 15px;
  }

  .header-row {
    flex-direction: column;
    gap: 15px;
    align-items: stretch;
  }

  .logo-text {
    text-align: center;
    font-size: 18px;
  }

  .header-buttons {
    flex-direction: column;
    width: 100%;
    gap: 8px;
  }

  .btn-share,
  .btn-download {
    justify-content: center;
    padding: 12px;
    width: 100%;
  }

  .report-title h1 {
    font-size: 24px;
  }

  .report-title p {
    font-size: 12px;
    padding: 0 10px;
  }

  /* Content wrapper - full width on mobile */
  .content-wrapper {
    margin: -130px auto 0;
    padding: 10px;
    width: 100%;
  }

  /* Business Info Card - Full width on mobile */
  .business-info-card {
    width: 100%;
    margin-bottom: 15px;
  }

  .business-content {
    flex-direction: column;
    height: auto;
    padding: 15px !important;
  }

  .business-left {
    padding: 0;
    margin-bottom: 20px;
  }

  .business-name {
    font-size: 18px;
    margin-bottom: 8px;
  }

  .business-url {
    font-size: 11px;
    margin-bottom: 12px;
  }

  /* Info grid - single column on mobile */
  .info-grid {
    grid-template-columns: 1fr;
    gap: 12px;
    margin-bottom: 15px;
  }

  .info-group label {
    font-size: 10px;
    margin-bottom: 2px;
  }

  .info-value {
    font-size: 11px;
  }

  /* Business photo on mobile */
  .business-right {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .business-photo-container {
    width: 100%;
    max-width: 300px;
    height: 200px;
  }

  .business-photo {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  /* Stats Grid - 2x2 on mobile */
  .stats-container {
    margin-bottom: 15px;
  }

  .stats-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
    margin-bottom: 10px;
  }

  .stat-card {
    padding: 12px;
  }

  .stat-label {
    font-size: 10px;
  }

  .stat-number {
    font-size: 18px;
  }

  .stat-change {
    font-size: 9px;
  }

  /* Summary and Chart - Full width stacked on mobile */
  .summary-chart-container {
    grid-template-columns: 1fr;
    gap: 15px;
    margin-bottom: 15px;
  }

  .summary-section,
  .chart-section {
    padding: 15px;
    width: 100%;
  }

  .summary-section h3,
  .chart-section h3 {
    font-size: 13px;
    margin-bottom: 10px;
  }

  .summary-section p {
    font-size: 11px;
    line-height: 1.4;
  }

  .summary-list li {
    font-size: 11px;
    margin-bottom: 6px;
  }

  /* Chart adjustments on mobile */
  .chart-wrapper {
    flex-direction: column;
    align-items: center;
    gap: 15px;
    padding: 15px;
  }

  .chart-left {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .chart-container {
    width: 120px;
    height: 120px;
  }

  .chart-right {
    width: 100%;
  }

  .chart-stats-row {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .chart-stat-item {
    text-align: center;
  }

  .chart-stat-value {
    font-size: 16px;
  }

  .chart-stat-label {
    font-size: 9px;
  }

  /* Map Section - Full width on mobile */
  .map-section {
    padding: 15px;
    margin-bottom: 15px;
  }

  .map-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 12px;
  }

  .map-header h3 {
    font-size: 14px;
  }

  .map-legend-inline {
    width: 100%;
    overflow-x: auto;
    padding-bottom: 5px;
  }

  .legend-item {
    padding: 5px 10px;
    font-size: 10px;
    white-space: nowrap;
  }

  .map-container {
    height: 300px;
    width: 100%;
  }

  /* Table Section - Responsive on mobile */
  .table-section {
    padding: 15px;
    overflow-x: auto;
  }

  .table-section h3 {
    font-size: 14px;
    margin-bottom: 8px;
  }

  .table-section p {
    font-size: 11px;
    margin-bottom: 12px;
  }

  /* Make table responsive with horizontal scroll */
  .competitors-table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .competitors-table {
    min-width: 600px;
    font-size: 11px;
  }

  .competitors-table th,
  .competitors-table td {
    padding: 8px 6px;
    font-size: 10px;
  }

  .data-table {
    min-width: 600px;
    font-size: 11px;
  }

  .data-table th,
  .data-table td {
    padding: 8px 6px;
    font-size: 10px;
  }

  .rank-badge {
    padding: 3px 6px;
    font-size: 9px;
  }

  .competitor-name {
    font-size: 11px;
  }

  .rating-cell {
    font-size: 10px;
  }

  /* Keywords Grid Section - Full width on mobile */
  .keywords-grid-section {
    padding: 15px;
  }

  .keyword-grid {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .keyword-card h4 {
    font-size: 12px;
  }

  .keyword-rank {
    font-size: 11px;
  }

  /* Footer adjustments */
  .report-footer {
    padding: 15px;
    font-size: 10px;
  }

  .footer-content {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }

  /* Loading and no data states */
  .loading-container,
  .no-data-container {
    padding: 20px;
  }

  .loading-container p,
  .no-data-card p {
    font-size: 12px;
  }

  .no-data-card h2 {
    font-size: 18px;
  }

  .back-btn {
    padding: 10px 20px;
    font-size: 12px;
  }

  /* Timeline navigation on mobile */
  .timeline-navigation {
    padding: 10px;
    overflow-x: auto;
  }

  .timeline-item {
    min-width: 100px;
    font-size: 11px;
  }

  .timeline-date {
    font-size: 10px;
  }

  /* Print and share modal adjustments */
  .share-url-input {
    font-size: 11px;
    padding: 10px;
  }

  .copy-btn {
    padding: 10px 15px;
    font-size: 11px;
  }
}

/* Additional mobile optimizations for very small screens */
@media (max-width: 480px) {
  .dark-header {
    padding: 15px;
    padding-bottom: 120px;
  }

  .report-title h1 {
    font-size: 20px;
  }

  .business-name {
    font-size: 16px;
  }

  .stat-number {
    font-size: 16px;
  }

  .map-container {
    height: 250px;
  }
}

/* PDF Export Styles */
.pdf-only {
  display: none;
}

.screen-only {
  display: inline;
}

/* Set background color for PDF export */
.generating-pdf {
  background: #f5f5f5 !important;
  min-height: 100vh !important;
}

/* Hide business-right section during PDF generation */
.generating-pdf .business-right {
  display: none !important;
}

/* Make business-left take full width during PDF generation */
.generating-pdf .business-left {
  width: 100% !important;
  flex: 1 1 100% !important;
}

/* Show PDF content and hide screen content when generating PDF */
.generating-pdf .pdf-only {
  display: inline !important;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
}

.generating-pdf .screen-only {
  display: none !important;
}

/* Limit column width for Additional Categories in PDF */
.generating-pdf .data-table th:nth-child(6),
.generating-pdf .data-table td:nth-child(6) {
  max-width: 300px;
  width: 300px;
  word-wrap: break-word;
  word-break: break-word;
  white-space: normal;
  overflow-wrap: break-word;
}

/* Hide Google Maps controls during PDF export */
.generating-pdf .gm-control-container,
.generating-pdf .gmnoprint,
.generating-pdf .gm-style-cc,
.generating-pdf .gm-bundled-control,
.generating-pdf .gm-svpc {
  display: none !important;
}

/* Fix legend positioning during PDF export */
.generating-pdf .map-legend-inline {
  display: flex !important;
  align-items: center !important;
  gap: 20px !important;
  flex-wrap: nowrap !important;
}

.generating-pdf .legend-item {
  display: inline-flex !important;
  align-items: center !important;
  vertical-align: middle !important;
  line-height: 1 !important;
}

.generating-pdf .legend-square {
  display: inline-block !important;
  vertical-align: middle !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
  position: relative !important;
  top: -1px !important;
}

.generating-pdf .legend-text {
  display: inline-block !important;
  vertical-align: middle !important;
  line-height: 16px !important;
  margin-top: -16px !important;
}
</style>